import { useEffect, useState } from 'react';
import { trackError } from '#api/errors.ts';
import { useTimeout } from '#hooks/useTimeout.ts';
import { copyToClipboard } from '#util/copy-to-clipboard.ts';
import { Tooltip, type Placement } from '../../tooltip/Tooltip.tsx';
import { CopyIconButton } from '../icon/index.ts';
import type { IconButtonProps } from '../icon/types.ts';

type Props = {
  value: string;
  placement?: Placement;
  initialTooltip?: string;
  successTooltip?: string;
} & IconButtonProps;

export function CopyButton({
  value,
  placement = 'right',
  initialTooltip = 'Copy',
  successTooltip = 'Copied!',
  ...buttonProps
}: Props) {
  const [tooltip, setTooltip] = useState(initialTooltip);
  const { start: resetTooltipAfterDelay, clear: clearTimeout } = useTimeout(() => {
    setTooltip(initialTooltip);
  }, 2000);

  // Reset the tooltip after 2 seconds
  useEffect(() => {
    if (tooltip !== initialTooltip) {
      resetTooltipAfterDelay();
    }
  }, [initialTooltip, resetTooltipAfterDelay, tooltip]);

  return (
    <Tooltip
      content={tooltip}
      // A click cancels the anchor's pending hover-show, so a quick click would otherwise copy with no
      // feedback. `undefined` hands control back to hover once the reset timer restores `initialTooltip`.
      open={tooltip !== initialTooltip || undefined}
      placement={placement}
      onHidden={() => {
        setTooltip(initialTooltip);
        clearTimeout();
      }}
      render={
        <CopyIconButton
          {...buttonProps}
          onClick={() => {
            copyToClipboard(value)
              .then(() => {
                return setTooltip(successTooltip);
              })
              .catch((err: unknown) => {
                console.error('Could not copy');
                setTooltip('Unable to copy');
                trackError(err);
              });
          }}
        />
      }
    />
  );
}
